import { CONFIG as config } from '../config';
import { buildUrl } from '../utils';
import { FileStorePluginApi, FileResponse } from './FileStorePluginApi';
import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';

/** @public */
export class FileStorePluginClient implements FileStorePluginApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  async getLatestLog(applianceId: string, date: string): Promise<FileResponse> {
    const url = buildUrl(config.baseUrl, {
      path: `/${applianceId}/logs/latest`,
      queryParams: { date },
    });
    try {
      const response = await this.get(url);
      return response.body;
    } catch (error) {
      return { fileURL: '', cDate: '', name: '' };
    }
  }

  async getAllLogs(applianceId: string, date: string): Promise<FileResponse[]> {
    const url = buildUrl(config.baseUrl, {
      path: `/${applianceId}/logs/all`,
      queryParams: { date },
    });
    try {
      const response = await this.get(url);
      return response.body;
    } catch (error) {
      return [];
    }
  }

  async createLogArchive(
    applianceId: string,
    date: string,
    fileNames: string[],
  ): Promise<FileResponse> {
    const url = buildUrl(config.baseUrl, {
      path: `/${applianceId}/logs/archive`,
    });
    const response = await this.post(url, { date, fileNames });
    return response.body;
  }

  async getLatestImage(
    applianceId: string,
    feature: string,
  ): Promise<FileResponse> {
    const url = buildUrl(config.baseUrl, {
      path: `/${applianceId}/images/latest`,
      queryParams: { feature },
    });
    try {
      const response = await this.get(url);
      return response.body;
    } catch (error) {
      return { fileURL: '', cDate: '', name: '' };
    }
  }

  async getImageByName(
    applianceId: string,
    imageName: string,
    feature: string,
  ): Promise<FileResponse> {
    const url = buildUrl(config.baseUrl, {
      path: `/${applianceId}/images/${imageName}`,
      queryParams: { feature },
    });
    try {
      const response = await this.get(url);
      return response.body;
    } catch (error) {
      return { fileURL: '', cDate: '', name: '' };
    }
  }

  async getLatestCapability(): Promise<FileResponse> {
    const url = buildUrl(config.baseUrl, { path: `/capabilities/latest` });
    try {
      const response = await this.get(url);
      return response.body;
    } catch (error) {
      return { fileURL: '', cDate: '', name: '' };
    }
  }

  async getAllCapabilities(): Promise<FileResponse[]> {
    const url = buildUrl(config.baseUrl, { path: `/capabilities/all` });
    try {
      const response = await this.get(url);
      return response.body;
    } catch (error) {
      return [];
    }
  }

  async get(path: string): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'GET',
    });

    if (!response.ok) {
      const payload = await response.text();
      const message = `Request failed with ${response.status} ${response.statusText}, ${payload}`;
      throw new Error(message);
    }

    const body = await response.json();

    if (body === undefined || body === null) {
      const message = `Request succeeded but returned an invalid response: body is ${body}`;
      throw new Error(message);
    }

    return { status: response.status, body };
  }

  async post(path: string, payload: object): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const responsePayload = await response.text();
      const message = `Request failed with ${response.status} ${response.statusText}, ${responsePayload}`;
      throw new Error(message);
    }

    const body = await response.json();
    if (body === undefined || body === null) {
      const message = `Request succeeded but returned an invalid response: body is ${body}`;
      throw new Error(message);
    }

    return { status: response.status, body };
  }
}
